import React, { useState, useMemo } from 'react';
import { 
  Box, Typography, Stack, Chip, Drawer, Pagination, Grid,
  Paper, InputBase, IconButton, FormControlLabel, Switch, Divider 
} from '@mui/material';
import { 
  Close as CloseIcon, Search as SearchIcon, 
  HistoryToggleOff as ScheduledIcon, DirectionsRun as ActualIcon,
  People as PeopleIcon, AccessTime as DurationIcon, 
  ReceiptLong as ReceiptIcon, AccountCircle as UserIcon,
  EventBusy as CancelIcon
} from '@mui/icons-material';

const PAGE_SIZE = 15;

const BillingAuditDrawer = ({ logData, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);
  const [page, setPage] = useState(1);

  const { allLogs, stats, exportedBy } = useMemo(() => {
    let logs = logData?.calculation_log;
    if (typeof logs === 'string') try { logs = JSON.parse(logs); } catch (e) { logs = []; }
    const processed = Array.isArray(logs) ? logs : [];
    
    return {
      allLogs: processed,
      exportedBy: logData?.exported_by || 'System',
      stats: processed.reduce((acc, curr) => {
        curr.status === 'failed' ? acc.failed++ : acc.success++;
        return acc;
      }, { success: 0, failed: 0 })
    };
  }, [logData]);

  const filteredLogs = useMemo(() => {
    return allLogs.filter(log => {
      const matchesSearch = log.client?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesIssue = showIssuesOnly ? log.status === 'failed' : true;
      return matchesSearch && matchesIssue;
    });
  }, [allLogs, searchTerm, showIssuesOnly]);

  const paginatedLogs = useMemo(() => {
    return filteredLogs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredLogs, page]);

  return (
    <Drawer 
      anchor="right" 
      open={!!logData} 
      onClose={onClose} 
      PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, bgcolor: '#f8fafc' } }}
    >
      {/* Header Section */}
      <Box sx={{ p: 2.5, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight="900" color="#1e293b">Billing Audit Log</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>

        {/* Exported By Info */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <UserIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight="700" color="text.secondary">
            Exported By: <span style={{ color: '#3b82f6' }}>{exportedBy}</span>
          </Typography>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', bgcolor: '#f0fdf4', borderColor: '#bbf7d0' }}>
              <Typography variant="caption" fontWeight="800" color="success.main">SUCCESS: {stats.success}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', bgcolor: '#fef2f2', borderColor: '#fecaca' }}>
              <Typography variant="caption" fontWeight="800" color="error.main">ISSUES: {stats.failed}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2}>
          <Paper elevation={0} sx={{ flex: 1, px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', bgcolor: '#f1f5f9', borderRadius: 2 }}>
            <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />
            <InputBase 
                placeholder="Search Client..." 
                fullWidth 
                onChange={(e) => {setSearchTerm(e.target.value); setPage(1);}} 
            />
          </Paper>
          <FormControlLabel 
            control={<Switch size="small" checked={showIssuesOnly} onChange={(e) => {setShowIssuesOnly(e.target.checked); setPage(1);}} />} 
            label={<Typography variant="caption" fontWeight="800">ISSUES ONLY</Typography>} 
          />
        </Stack>
      </Box>

      {/* Logs List */}
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          {paginatedLogs.map((log, i) => {
            const isCancelled = log.actual === 'CANCELLED';
            
            return (
              <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: log.status === 'failed' ? '#fffafb' : '#fff' }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="900">{log.client}</Typography>
                    <Typography variant="caption" color="textSecondary" fontWeight="700">{log.visit_date}</Typography>
                  </Box>
                  <Chip 
                    label={isCancelled ? "CANCELLED-DPC" : log.status?.toUpperCase()} 
                    size="small" 
                    color={log.status === 'failed' ? "error" : isCancelled ? "warning" : "success"} 
                    sx={{ fontWeight: 900, fontSize: 10 }} 
                  />
                </Stack>

                <Grid container spacing={1} sx={{ mb: 1.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #f1f5f9' }}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="textSecondary" fontWeight="800" sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScheduledIcon sx={{ fontSize: 12, mr: 0.5 }} /> SCHED
                    </Typography>
                    <Typography variant="body2" fontWeight="700" sx={{ fontSize: '0.75rem' }}>{log.scheduled}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="textSecondary" fontWeight="800">RESCHED</Typography>
                    <Typography variant="body2" fontWeight="700" sx={{ fontSize: '0.75rem' }} color="secondary">{log.rescheduled}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="textSecondary" fontWeight="800" sx={{ display: 'flex', alignItems: 'center' }}>
                        <ActualIcon sx={{ fontSize: 12, mr: 0.5 }} /> ACTUAL
                    </Typography>
                    <Typography 
                        variant="body2" 
                        fontWeight="900" 
                        sx={{ fontSize: '0.75rem' }} 
                        color={isCancelled ? "warning.main" : "success.main"}
                    >
                      {log.actual || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Rate Breakdown / Summary Section */}
                {log.summary && (
                  <Box sx={{ mb: 1.5, p: 1, bgcolor: isCancelled ? '#fff7ed' : '#f1f5f9', borderRadius: 1.5, borderLeft: `4px solid ${isCancelled ? '#f59e0b' : '#3b82f6'}` }}>
                     <Typography variant="caption" fontWeight="800" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <ReceiptIcon sx={{ fontSize: 14, mr: 0.5 }} /> 
                      {isCancelled ? "BILLING NOTE" : "RATE BREAKDOWN (Per Carer)"}
                    </Typography>
                    <Typography variant="caption" fontWeight="700" color={isCancelled ? "warning.dark" : "primary.main"}>
                      {log.summary}
                    </Typography>
                  </Box>
                )}

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1}>
                    <Chip icon={<DurationIcon sx={{ fontSize: 12 }} />} label={log.duration} size="small" sx={{ fontWeight: 800, height: 24 }} />
                    <Chip icon={<PeopleIcon sx={{ fontSize: 12 }} />} label={`${log.carers} Carers`} size="small" sx={{ fontWeight: 800, height: 24 }} />
                  </Stack>
                  {log.total && (
                    <Typography variant="h6" fontWeight="900" color={isCancelled ? "#f59e0b" : "#059669"}>
                        {log.total}
                    </Typography>
                  )}
                </Stack>
                
                {log.error && (
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <CancelIcon sx={{ fontSize: 14, color: '#ef4444', mt: 0.2 }} />
                        <Typography variant="caption" color="error" fontWeight="800">
                            {log.error}
                        </Typography>
                    </Box>
                )}
              </Paper>
            );
          })}
        </Stack>

        {filteredLogs.length > PAGE_SIZE && (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <Pagination 
                count={Math.ceil(filteredLogs.length / PAGE_SIZE)} 
                page={page} 
                onChange={(_, v) => { setPage(v); document.querySelector('.MuiDrawer-paper').scrollTo(0,0); }} 
                size="small" 
                color="primary" 
            />
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

export default BillingAuditDrawer;